/**
 * Collects layout inputs (viewport width, legend width, right occluded)
 * and fires a single callback when all required values are available.
 *
 * Phase 1 (pre-resolved): stores values, resolves wait() when both
 *   viewport and legend widths are set.
 * Phase 2 (post-resolved): set* calls notify immediately unless held for
 * motion. Releasing the last hold emits the latest geometry once. A new
 * sheet bind resets the holds and still resolves its initial geometry.
 */

import { nonnull } from "@tento-core/assert";
import type { Option } from "@tento-lona/sheets";
export class SheetLayoutGate {
  #viewportWidth: Option<number> = null;
  #legendWidth: Option<number> = null;
  #rightOccludedPx: number = 0;
  #resolved = false;
  #resolver: Option<(props: SheetLayoutGate.Props) => void> = null;
  #pending: Option<Promise<SheetLayoutGate.Props>> = null;
  #holds = new Set<object>();
  #layoutPending = false;
  onChange: Option<(props: SheetLayoutGate.Props) => void> = null;

  /** Keep inputs current while delaying resize notifications until motion ends. */
  holdUpdates(): () => void {
    const hold = {};
    this.#holds.add(hold);
    return () => {
      if (!this.#holds.delete(hold) || this.#holds.size > 0) return;
      if (!this.#layoutPending) return;
      this.#layoutPending = false;
      this.#notifyChange();
    };
  }

  #notifyChange(): void {
    if (this.#holds.size > 0) {
      this.#layoutPending = true;
      return;
    }
    const props = this.#snapshot();
    if (props) this.onChange?.(props);
  }

  get isResolved(): boolean {
    return this.#resolved;
  }

  setViewportWidth(w: number): void {
    this.#viewportWidth = w;
    this.#emit();
  }

  setLegendWidth(w: number): void {
    this.#legendWidth = w;
    this.#emit();
  }

  setRightOccludedPx(px: number): void {
    this.#rightOccludedPx = px;
    if (this.#resolved) this.#notifyChange();
  }

  /** Re-emit the current resolved props so the onChange closure re-runs
   *  applyLayout against fresh viewmodel state. No-op while unresolved or
   *  while onChange is null (during a rebind). */
  refresh(): void {
    if (this.#resolved) this.#notifyChange();
  }

  wait(): Promise<SheetLayoutGate.Props> {
    if (this.#viewportWidth != null && this.#legendWidth != null) {
      this.#resolved = true;
      return Promise.resolve(nonnull(this.#snapshot(), "both widths are known"));
    }
    if (!this.#pending) {
      this.#pending = new Promise((r) => (this.#resolver = r));
    }
    return this.#pending;
  }

  reset(): void {
    this.#holds.clear();
    this.#layoutPending = false;
    this.#viewportWidth = null;
    this.#legendWidth = null;
    this.#resolved = false;
    this.#resolver = null;
    this.#pending = null;
    this.onChange = null;
  }

  #emit(): void {
    if (this.#resolved) {
      this.#notifyChange();
      return;
    }
    if (this.#viewportWidth == null || this.#legendWidth == null) return;
    this.#resolved = true;
    this.#resolver?.(nonnull(this.#snapshot(), "both widths are known"));
    this.#resolver = null;
    this.#pending = null;
  }

  #snapshot(): Option<SheetLayoutGate.Props> {
    if (this.#viewportWidth == null || this.#legendWidth == null) return null;
    return {
      viewportWidth: this.#viewportWidth,
      legendWidth: this.#legendWidth,
      rightOccludedPx: this.#rightOccludedPx,
    };
  }
}

export namespace SheetLayoutGate {
  export type Props = {
    viewportWidth: number;
    legendWidth: number;
    rightOccludedPx: number;
  };
}
